import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import OAuthButton from './shared/OAuthButton';

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  text-align: center;
  line-height: 32px;
  margin-bottom: 4px;
`;

const SubHeading = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.theme.textSecondary};
  text-align: center;
  line-height: 24px;
  margin-bottom: 24px;
`;

const AppName = styled.span`
  color: ${p => p.theme.text};
  font-weight: 500;
`;

const ViaLabel = styled.p`
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  text-align: center;
  margin-top: 2px;
  margin-bottom: 8px;
  font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
`;

const ClientIconImg = styled.img`
  display: block;
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  border-radius: ${p => p.theme.radius};
  object-fit: contain;
`;

const PermissionList = styled.div`
  margin-bottom: 24px;
`;

const PermissionLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.text};
  margin-bottom: 12px;
`;

const PermissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
`;

const CheckIcon = styled.span`
  color: ${p => p.theme.accent};
  font-size: 16px;
  flex-shrink: 0;
`;

const PermissionText = styled.span`
  font-size: 14px;
  color: ${p => p.theme.textSecondary};
  line-height: 20px;
`;

const SignedInAs = styled.div`
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.textTertiary};
  margin-bottom: 20px;
  line-height: 18px;
`;

const SignedInEmail = styled.span`
  color: ${p => p.theme.textSecondary};
`;

const OptionalSection = styled.div`
  margin-bottom: 24px;
`;

const OptionalLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.text};
  margin-bottom: 12px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
`;

const ToggleSwitch = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: ${p => p.active ? p.theme.accent : 'rgba(255,255,255,0.15)'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.active ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const DenyLink = styled.button`
  display: block;
  margin: 12px auto 0;
  background: none;
  border: none;
  color: ${p => p.theme.textTertiary};
  font-family: ${p => p.theme.fontFamily};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;

  &:hover { color: ${p => p.theme.textSecondary}; }
`;

const HiddenForm = styled.form`
  display: none;
`;

export default function ConsentStep({ clientName, appName, clientIconSrc, permissions, optionalScopes, userEmail, csrfToken, onApproved }) {
  const [loading, setLoading] = useState(false);
  const [optionalEnabled, setOptionalEnabled] = useState({});
  const approveFormRef = useRef(null);
  const denyFormRef = useRef(null);

  // Use native form submission so the browser follows the 302 redirect
  // to the client's redirect_uri (which is a different origin).
  // If the redirect opens an external app, the page stays — show success after 3s.
  var handleApprove = function() {
    setLoading(true);
    approveFormRef.current.submit();
    if (onApproved) {
      setTimeout(onApproved, 3000);
    }
  };

  var handleDeny = function() {
    denyFormRef.current.submit();
  };

  // Extract CSRF token from meta tag (set by Rails layout)
  var csrf = csrfToken || (document.querySelector('meta[name="csrf-token"]') || {}).content || '';

  return (
    <div>
      {clientIconSrc && (
        <ClientIconImg src={clientIconSrc} alt="" />
      )}
      <Heading>
        <AppName>{clientName}</AppName>
      </Heading>
      {appName && clientName !== appName && (
        <ViaLabel>via {appName}</ViaLabel>
      )}
      <SubHeading>wants access to your account</SubHeading>

      <PermissionList>
        <PermissionLabel>This will allow {clientName} to:</PermissionLabel>
        {(permissions || []).map(function(perm, i) {
          return (
            <PermissionItem key={i}>
              <CheckIcon>&#10003;</CheckIcon>
              <PermissionText>{perm}</PermissionText>
            </PermissionItem>
          );
        })}
      </PermissionList>

      {(optionalScopes || []).length > 0 && (
        <OptionalSection>
          <OptionalLabel>Optional permissions:</OptionalLabel>
          {optionalScopes.map(function(group) {
            var enabled = optionalEnabled[group.scope] || false;
            return (
              <ToggleRow key={group.scope}>
                <PermissionText>{group.permissions[0]}</PermissionText>
                <ToggleSwitch
                  active={enabled}
                  onClick={function() {
                    var next = Object.assign({}, optionalEnabled);
                    next[group.scope] = !enabled;
                    setOptionalEnabled(next);
                  }}
                  type="button"
                />
              </ToggleRow>
            );
          })}
        </OptionalSection>
      )}

      <SignedInAs>
        Signed in as <SignedInEmail>{userEmail}</SignedInEmail>
      </SignedInAs>

      <OAuthButton onClick={handleApprove} loading={loading}>
        Allow access
      </OAuthButton>

      <DenyLink onClick={handleDeny}>
        Deny
      </DenyLink>

      {/* Hidden forms for native browser redirect */}
      <HiddenForm ref={approveFormRef} method="POST" action="/oauth/web/approve">
        <input type="hidden" name="authenticity_token" value={csrf} />
        {(optionalScopes || []).map(function(group) {
          return (
            <input
              key={group.scope}
              type="hidden"
              name={'scope_' + group.scope}
              value={optionalEnabled[group.scope] ? '1' : '0'}
            />
          );
        })}
      </HiddenForm>

      <HiddenForm ref={denyFormRef} method="POST" action="/oauth/web/deny">
        <input type="hidden" name="authenticity_token" value={csrf} />
      </HiddenForm>
    </div>
  );
}
